/** Edit a preflight user group's membership.
 *
 * A multi-select of **system users** (internal staff) saved via `set_members`.
 * The backend re-validates system-users-only, so a tenant-bound user chosen here
 * (should be impossible via the picker) is still rejected server-side. */
import { Alert, Box, Button, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { userGroups, type PreflightUserGroup } from '@/api/preflight';
import { list as listUsers } from '@/api/users';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import { userDisplayName } from '@/features/users/userName';
import { usePermissions } from '@/hooks/usePermissions';

interface MemberOption {
  id: number;
  email: string;
  label: string;
}

interface Props {
  open: boolean;
  group: PreflightUserGroup | null;
  onClose: () => void;
}

export function PreflightUserGroupMembersDialog({ open, group, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [selected, setSelected] = useState<MemberOption[]>([]);
  const history = useEditorHistoryTabs({
    enabled: group !== null && hasPerm('console_audit_view'),
    contentType: 'demo.preflight_user_group',
    objectPk: group?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['preflight', 'user-groups'] }),
  });

  // Re-seed from the group's current members each time the dialog opens.
  useEffect(() => {
    if (open) history.reset();
    if (open && group) {
      setSelected(group.members.map((m) => ({ id: m.id, email: m.email, label: m.name || m.email })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- history.reset is per-render
  }, [open, group]);

  const mutation = useMutation({
    mutationFn: (ids: number[]) => {
      if (!group) throw new Error('no group selected');
      return userGroups.setMembers(group.id, ids);
    },
    onSuccess: () => {
      snackbar.show('Members updated.', 'success');
      qc.invalidateQueries({ queryKey: ['preflight', 'user-groups'] });
      onClose();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const searchSystemUsers = async (query: string): Promise<MemberOption[]> => {
    const res = await listUsers({ kind: 'system', search: query, pageSize: 20, is_active: true });
    return res.results.map((u) => ({ id: u.id, email: u.email, label: userDisplayName(u) }));
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={group ? `Members · ${group.name}` : 'Members'}
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(selected.map((s) => s.id))}
            >
              {mutation.isPending ? 'Saving…' : 'Save members'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: { sm: 460 } }}>
        <Alert severity="info">
          Only system users (internal staff) can be members. Each new tour operator&apos;s onboarding items in the
          <strong> {group?.name} </strong> role are assigned to this group, and every member is emailed a summary.
        </Alert>
        <RemoteAutocomplete<MemberOption>
          multiple
          fetchOnEmpty
          label="Members"
          placeholder="Search system users by name or email"
          value={selected}
          onChange={(v) => setSelected((v as MemberOption[]) ?? [])}
          queryFn={(q) => searchSystemUsers(q)}
          getOptionLabel={(o) => o.label}
          getOptionKey={(o) => o.id}
        />
        <Typography variant="caption" color="text.secondary">
          {selected.length} member{selected.length === 1 ? '' : 's'} selected.
        </Typography>
      </Box>
      )}
    </AppDialog>
  );
}
